'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';
import { company, addressLine } from '@/lib/company';

const policyContent = [
  {
    id: 'information-collection',
    title: 'Information Collection',
    content: `We collect personal data including your name, email address, and phone number when you register on our platform or contact us. Additionally, we may gather non-personal information about your interactions with our services, such as device information, browser type, and browsing behavior.`,
  },
  {
    id: 'use-of-information',
    title: 'Use of Information',
    content: `The information we collect is used to provide and improve our services, respond to your inquiries, ensure the security of our platform, and communicate with you about updates and newsletters (if you have opted in to receive them).`,
  },
  {
    id: 'data-security',
    title: 'Data Security',
    content: `We take the security of your personal information seriously. We employ industry-standard encryption and other security measures to protect your data from unauthorized access, disclosure, or alteration. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.`,
  },
  {
    id: 'data-retention',
    title: 'Data Retention',
    content: `We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. For example, we retain account information for as long as your account is active. When we no longer have a legitimate business need to process your personal information, we will either delete or anonymize it.`,
  },
  {
    id: 'data-sharing',
    title: 'Data Sharing',
    content: `We do not share, sell, or trade your personal information with third parties for marketing purposes. We may share your data with trusted service providers who assist us in operating our website and services, provided they agree to keep your information confidential.`,
  },
  {
    id: 'cookies',
    title: 'Cookies and Tracking Technologies',
    content: `We use cookies and similar tracking technologies to enhance your experience on our platform. These technologies help us analyze website traffic, remember your preferences, and deliver personalized content. You can control cookies through your browser settings, but disabling them may affect the functionality of certain features.`,
  },
  {
    id: 'third-party-links',
    title: 'Third-Party Links',
    content: `Our website may contain links to external websites. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review their privacy policies before providing any personal information.`,
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    content: `You have the right to access, update, or delete your personal information at any time. If you wish to exercise these rights or have any concerns about your data, please contact us using the information provided below.`,
  },
  {
    id: 'policy-changes',
    title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. Any changes will be posted on this page, and we will notify you of significant updates. We encourage you to review this policy periodically.`,
  },
];

export function PrivacyPolicyClient() {
  const pageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!pageRef.current) return;

    const ctx = gsap.context(() => {
      // Animate title letters - same as home page
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        const letterTimeline = gsap.timeline({ delay: 0.5 });

        shuffledLetters.forEach((letter, index) => {
          letterTimeline.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
          }, index * 0.05);
        });

        // Apply hover effect after animation
        letterTimeline.call(() => {
          applyHoverEffect(letters);
        });
      }

      // Animate content sections
      if (contentRef.current) {
        const sections = contentRef.current.querySelectorAll('.policy-section');
        sections.forEach((section, index) => {
          gsap.set(section, { y: 30, opacity: 0 });
          gsap.to(section, {
            scrollTrigger: {
              trigger: section,
              start: 'top 90%',
            },
            y: 0,
            opacity: 1,
            duration: 0.5,
            delay: index * 0.03,
            ease: 'power3.out',
          });
        });
      }
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} style={{ backgroundColor: '#0c0c0c' }}>
      {/* Hero Section */}
      <section
        className="relative flex flex-col items-center justify-center text-center"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: '180px',
          paddingBottom: '100px',
        }}
      >
        <div className="content-padding w-full">
          <h1
            ref={titleRef}
            className="hero-title font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white lowercase"
          >
            {'PRIVACY POLICY'.split('').map((char, i) => (
              <span
                key={i}
                className={`letter inline-block ${char === ' ' ? 'mx-[2vw]' : ''}`}
                style={{ transformOrigin: 'center bottom' }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </h1>
        </div>

        {/* Description and date - Left aligned */}
        <div className="content-padding w-full" style={{ marginTop: '80px' }}>
          <div className="max-w-4xl mx-auto">
            <div className="max-w-md">
              <p className="text-white/50 text-lg leading-relaxed text-left">
                Your privacy matters to us. This policy explains how DILACODE INC. collects, uses and protects your information.
              </p>
              <p className="text-white/30 text-sm text-left mt-4">
                Effective Date: May 30, 2026
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingBottom: '80px' }}>
        <div className="content-padding">
          <div className="max-w-3xl mx-auto" ref={contentRef}>

            {/* Who We Are */}
            <div className="policy-section pb-12 mb-12" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-2xl font-semibold text-white mb-4">
                Who We Are
              </h2>
              <p className="text-white/60 text-lg leading-relaxed">
                DILACODE INC., a corporation registered in Ontario, Canada (Ontario corporation number 1000959255), is the organization responsible for personal information collected through this website and our services. This Privacy Policy sets out what we collect, why, and how we safeguard it. By using our services you consent to the practices described here. If you would like a copy of the information we hold about you, or want it corrected or deleted, write to us at the address below.
              </p>
            </div>

            {/* Policy Sections */}
            {policyContent.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="policy-section pb-10 mb-10"
                style={{
                  borderBottom: index < policyContent.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                    style={{ backgroundColor: '#173835', color: '#fff' }}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-white mb-3">
                      {section.title}
                    </h2>
                    <p className="text-white/60 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Contact Section - Green Background, Centered */}
      <section
        style={{
          backgroundColor: '#173835',
          paddingTop: '100px',
          paddingBottom: '100px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <h2
            className="font-six-caps text-[calc(1rem+10vw)] md:text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase"
            style={{ textAlign: 'center', marginBottom: '32px' }}
          >
            Contact Us
          </h2>
          <p
            className="text-white/70 text-lg leading-relaxed"
            style={{ textAlign: 'center', maxWidth: '500px', marginBottom: '48px', padding: '0 24px' }}
          >
            If you have any questions or concerns about this Privacy Policy, please don&apos;t hesitate to reach out.
          </p>

          {/* Contact Info - Centered */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              marginBottom: '48px',
            }}
            className="sm:flex-row sm:gap-10"
          >
            <a
              href={`mailto:${company.email}`}
              className="flex items-center gap-3 text-white hover:text-white/80 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{company.email}</span>
            </a>

            <span className="hidden sm:block text-white/30">|</span>

            <div className="flex items-center gap-3 text-white/70">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{addressLine}</span>
            </div>
          </div>

          {/* Back to Home & Arabic Version - Centered */}
          <div className="flex flex-col items-center gap-4">
            <Link
              href="/privacy-policy-ar"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              النسخة العربية
            </Link>
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm uppercase tracking-wider"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
